import {
  List,
  Datagrid,
  TextInput,
  TextField,
  Pagination,
  useListContext
} from 'react-admin';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles
} from '@material-ui/core';
import React, { useMemo, useState } from 'react';

import GridPopover from '../../core/components/grid/GridPopover';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import LinkField from '../../core/components/LinkField';
import ListTitleBar from './fundinvestortagimport/ExcelFundInvestorTagImportTitleBarWithDropBox';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';

import { FundInvestorTagIcon } from '../../core/themes/icons/FundInvestorTagIcon';
import { ChevronDownIcon } from '../../core/themes/icons/ChevronDownIcon';
import { ChevronRightIcon } from '../../core/themes/icons/ChevronRightIcon';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';

const useStyles = makeStyles({
  nameColumnStyle: {
    width: '45%'
  },
  inputCustomWidth: {
    width: '25%'
  },
  formFullWidth: {
    width: '100%',
    marginTop: '0px',
    marginBottom: '-8px',
    '& > div': {
      width: '100%'
    }
  },
  tag: {
    color: '#212121',
    'font-size': '12px',
    'line-height': '12px',
    textDecoration: 'none',
    'box-sizing': 'border-box',
    background: '#F5F5F5',
    border: '1px solid #E0E0E0',
    'border-radius': '2px',
    padding: '0 5px'
  },
  fundInvestors: {
    color: '#2962FF',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  button: {
    padding: 0,
    justifyContent: 'flex-start'
  },
  paper: {
    '& > div': {
      maxHeight: '300px'
    }
  },
  cellChild: {
    paddingLeft: '36px !important',
    paddingRight: '0px !important'
  },
  expandCell: {
    cursor: 'pointer',
    marginRight: '5px'
  }
});

const classChevron = {
  chevron: {
    fontSize: '10px',
    color: '#757575',
    transform: 'scale(0.7)'
  }
};

const TagsTitle = props => {
  const { selectedIds } = useListContext();
  return (
    <ListTitleBar
      type="Fund-Investor Relationship Tags"
      title="Create New Fund-Investor Tag"
      createUrl="#/fundinvestortags/create"
      icon={<FundInvestorTagIcon />}
      hasCreatePermission={props.hasCreatePermission}
      hasImportFundInvestorTagExcelPermission={
        props.hasImportFundInvestorTagExcelPermission
      }
      hasExportFundInvestorTagExcelPermission={
        props.hasExportFundInvestorTagExcelPermission
      }
      filters={props.filters}
      fundInvestorTagNameFilter={props.fundInvestorTagNameFilter}
      selectedIds={selectedIds}
    />
  );
};

const FundInvestorTagNameFilters = props => {
  const classes = useStyles();

  const handleNameFilterChange = event => {
    props.setFundInvestorTagNameFilter(event.target.value);
  };

  return (
    <Filter {...props} variant="standard" className={classes.formFullWidth}>
      <TextInput
        label="Search by Fund-Investor Tag Name"
        source="name"
        onChange={handleNameFilterChange}
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
        className={classes.inputCustomWidth}
      />
    </Filter>
  );
};

const Investors = ({ investors }) => {
  const classes = useStyles();
  // sorting the investors alphabetically case-insensitive
  return investors
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map(investor => {
      return (
        <TableRow key={investor}>
          <TableCell classes={{ root: classes.cellChild }}>
            {investor}
          </TableCell>
        </TableRow>
      );
    });
};

export const Funds = ({ fundName, investorNames }) => {
  const classes = useStyles();
  const [expand, setExpand] = useState(false);

  const handleExpand = () => {
    setExpand(!expand);
  };

  const RenderChevron = () => {
    return expand ? (
      <ChevronDownIcon style={classChevron.chevron} />
    ) : (
      <ChevronRightIcon style={classChevron.chevron} />
    );
  };

  return (
    <>
      <TableRow>
        <TableCell style={{ width: '66%' }}>
          <Grid container alignItems="center">
            <Grid
              item
              onClick={handleExpand}
              classes={{ root: classes.expandCell }}
            >
              <RenderChevron />
            </Grid>
            <Grid item>{fundName}</Grid>
          </Grid>
        </TableCell>
      </TableRow>
      {expand && <Investors investors={investorNames} />}
    </>
  );
};

const DisplayFundInvestors = ({ record }) => {
  function renderFundInvestors() {
    /**
     * Build a map[key, value] from fetched fund investor relation
     * where key = fund name & value = array of investor name
     */
    const mapFundInvestorsNames = record.fundInvestors.reduce((acc, fi) => {
      if (!acc[fi.fundName]) {
        acc[fi.fundName] = [];
      }
      acc[fi.fundName].push(fi.investorName);
      return acc;
    }, {});

    return (
      <>
        {Object.entries(mapFundInvestorsNames)
          .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()))
          .map(([fundName, investors]) => (
            <Funds
              key={fundName}
              fundName={fundName}
              investorNames={investors}
            />
          ))}
      </>
    );
  }

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Fund-Investor Relationships</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderFundInvestors()}</TableBody>
      </Table>
    </Paper>
  );
};

const FundInvestorField = ({ record }) => {
  const classes = useStyles();

  return record?.fundInvestors?.length >= 1 ? (
    <GridPopover
      text={`${record?.fundInvestors?.length} Fund-Investor relationships`}
      classes={{ paper: classes.paper, button: classes.button }}
      popoverContent={<DisplayFundInvestors record={record} />}
    />
  ) : (
    <span>0 Fund-Investor Relationships</span>
  );
};

const FundInvestorTagList = props => {
  const classes = useStyles();
  const { hasCreatePermission, hasDeletePermission, basePath } = props;

  const { hasImportFundInvestorTagExcelPermission } = useMemo(() => {
    return {
      hasImportFundInvestorTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_FUND_INVESTOR_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportFundInvestorTagExcelPermission } = useMemo(() => {
    return {
      hasExportFundInvestorTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_FUND_INVESTOR_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const [fundInvestorTagNameFilter, setFundInvestorTagNameFilter] = useState(
    ''
  );

  return (
    <List
      hasCreate={false}
      basePath={basePath}
      title={
        <TagsTitle
          hasCreatePermission={hasCreatePermission}
          hasImportFundInvestorTagExcelPermission={
            hasImportFundInvestorTagExcelPermission
          }
          filters={props.appliedFilters}
          hasExportFundInvestorTagExcelPermission={
            hasExportFundInvestorTagExcelPermission
          }
          fundInvestorTagNameFilter={fundInvestorTagNameFilter}
        />
      }
      sort={{ field: 'name', order: 'ASC' }}
      exporter={false}
      perPage={20}
      filters={
        <FundInvestorTagNameFilters
          setFundInvestorTagNameFilter={setFundInvestorTagNameFilter}
        />
      }
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={
        hasDeletePermission && (
          <BulkActionDeleteButton confirmContent="ra.message.confirm_delete_tags" />
        )
      }
      empty={<Empty />}
    >
      <Datagrid>
        <LinkField
          label="Fund-Investor Tag Name"
          source="name"
          headerClassName={classes.nameColumnStyle}
          className={classes.tag}
        />
        <TextField label="Description" source="description" />
        <FundInvestorField label="Assigned To:" />
      </Datagrid>
    </List>
  );
};

export default FundInvestorTagList;
